/* eslint-disable max-lines-per-function */
import React from "react";
import { navigate, useLocation } from "@reach/router";

import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import PopupState, { bindHover, bindMenu, bindTrigger } from "material-ui-popup-state";
import HoverMenu from "material-ui-popup-state/HoverMenu";
import { PopupState as PopupStateType } from "material-ui-popup-state/core";
import { ListItem } from "@material-ui/core";
import { Viewer, ScheduledUpdate } from "../auth/types";
import { AppLink, headerButtonStyles, isLinkActive } from "./navigation";
import { useMobileOpen } from "./mobile-open-context";

interface Props {
  position?: "left" | "right";
  viewer?: Viewer;
  href: string;
  waitlist?: boolean;
  mobile?: boolean;
  children: string;
}

type ListItem = {
  id: string;
  name: string;
  scheduledUpdate: ScheduledUpdate | null;
}
interface SubMenuProps {
  mobile?: boolean;
  position?: "left" | "right";
  lists: ListItem[],
  href: string;
}

const SubMenuItems: React.FC<{href: string; lists: ListItem[]; popupState: PopupStateType}> =
({ href, lists, popupState }) => {
  const { handleClose } = useMobileOpen();
  const includeAll = href === "/dash/manager/lists";
  const sortedLists = lists.sort((a, b) => a.name.localeCompare(b.name));
  const listOptions = includeAll ? [{ id: "all", name: "All" }, ...sortedLists] : sortedLists;

  return (<>{
    listOptions.map(({ id, name }) => {
      const link = `${href}/${id}`;
      const isActive = location.pathname.match(link);
      return (
        <MenuItem
          selected={!!isActive}
          href={link}
          key={id}
          onClick={() => {
            popupState.close();
            handleClose();
            navigate(link);
          }}
        >{name}</MenuItem>
      );
    })}</>);
};

const SubMenuPopupState: React.FC<SubMenuProps> = ({ mobile = false, lists, href, children, position = "right" }) => {
  const location = useLocation();

  return (
    <PopupState variant="popover" popupId={`sub-menu-${href.replace("/", "-")}`}>
      {(popupState) => (mobile ? (<>
        <a href="#">
          <MenuItem
            selected={isLinkActive(location.pathname, href)}
            {...bindTrigger(popupState)}
          >
            {children}
          </MenuItem>
        </a>
        <Menu {...bindMenu(popupState)}
          transformOrigin={{
            vertical: "bottom",
            horizontal: position,
          }}>
          <SubMenuItems href={href} lists={lists} popupState={popupState} />
        </Menu>
      </>) : (
        <>
          <Button
            style={{ ...headerButtonStyles, color: isLinkActive(location.pathname, href) ? "#37B4C9" : "white" }}
            color="inherit"
            {...bindHover(popupState)}
          >
            {children}
          </Button>
          <HoverMenu {...bindMenu(popupState)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: position,
            }}
            transformOrigin={{
              vertical: 0,
              horizontal: position,
            }}>
            <SubMenuItems href={href} lists={lists} popupState={popupState} />
          </HoverMenu>
        </>)
      )}
    </PopupState>
  );
};

export const ListsNavItem: React.FC<Props> = ({ mobile = false, viewer, children, href, waitlist = false, position = "right" }) => {
  const lists = viewer?.lists?.filter(
    (list) => !waitlist || list.scheduledUpdate?.enabled,
  ) ?? [];

  const listsLength = lists.length;
  if (listsLength > 1) {
    return <SubMenuPopupState
      lists={lists}
      href={href}
      position={position}
      mobile={mobile}
    >{children}</SubMenuPopupState>;
  }

  return (<AppLink href={href}>{children}</AppLink>);
};
